<template>
    <div class="sidebar-container">
        <aside class="sidebar d-flex flex-column p-1 pt-5"
            :class="[{ 'expanded': isExpanded }, { 'company-variant': user && (user.is_company || user.is_agent) }]"
            v-on-clickaway="() => {
                if (isScreenLowerThanDesktop) isExpanded = false;
            }" @mouseenter="onSidebarMouseEnter" @mouseleave="onSidebarMouseLeave">
            <a href="/" class="logo hide-on-minimize mb-4 mx-auto w-100 px-3">
                <img v-if="user && (user.is_company || user.is_agent)" src="/img/logo-gold.png" alt="logo" width="220">
                <Logo v-else />
            </a>
            <slot>
                <!-- Default sidebar menu, used for logged in users -->
                <div class="mb-3 sidebar-content" v-if="user">
                    <ul class="sidebar__nav-list ps-0 d-flex flex-column align-items-center mb-0">
                        <li class="hide-on-maximize">
                            <a href="/" class="sidebar__nav-list-item">
                                <img class="short-logo" src="/img/logo-short.webp" width="25px" alt="logo" />
                            </a>
                        </li>
                        <li>
                            <a href="/dashboard" class="sidebar__nav-list-item"
                                :class="{ active: $route.path.includes('/dashboard') }">
                                <img src="/img/icons/dashboard.svg" width="25" />
                                <span class="hide-on-minimize">Dashboard</span>
                            </a>
                        </li>
                        <li>
                            <a href="/chat" class="sidebar__nav-list-item"
                                :class="{ active: ['chat', 'agentChat'].includes($route.name) }">
                                <img src="/img/icons/chats.svg" width="25" />
                                <span class="hide-on-minimize">Chats</span>
                            </a>
                        </li>
                    </ul>

                    <template v-if="user.is_company || user.is_agent">
                        <a class="d-flex justify-content-between mt-4 w-100 hide-on-minimize group-title" href="#"
                            @click.prevent="showPremiumPlanItems = !showPremiumPlanItems">
                            <span> Premium Plan Tools </span>

                            <svg v-if="showPremiumPlanItems" class="flex-shrink-0" width="24" height="24"
                                xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                                <path
                                    d="M11.9999 13.1714L16.9497 8.22168L18.3639 9.63589L11.9999 15.9999L5.63599 9.63589L7.0502 8.22168L11.9999 13.1714Z">
                                </path>
                            </svg>

                            <svg v-else class="flex-shrink-0" width="24" height="24" xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24" fill="currentColor">
                                <path
                                    d="M13.1717 12.0007L8.22192 7.05093L9.63614 5.63672L16.0001 12.0007L9.63614 18.3646L8.22192 16.9504L13.1717 12.0007Z">
                                </path>
                            </svg>
                        </a>

                        <ul id="premium-plan-items"
                            class="sidebar__nav-list ps-0 d-flex flex-column align-items-center mb-0 pro-features"
                            :class="{ 'disabled': !(user.prospecting_tool_attributes && user.prospecting_tool_attributes.isActive) }"
                            @click="openActivateProspectingIfDisabled">
                            <li>
                                <a href="/prospecting-tool" class="sidebar__nav-list-item pro-feature"
                                    :class="{ active: $route.path.includes('/prospecting-tool') }">
                                    <img src="/img/icons/prospecting-tool.svg" width="25" />
                                    <span class="hide-on-minimize">Stock Finder</span>
                                </a>
                            </li>
                            <li>
                                <a href="#" @click.prevent="onLetterCampaignsClick"
                                    class="sidebar__nav-list-item pro-feature"
                                    :class="{ active: $route.path.includes('/letter-sending/overview') }">
                                    <img src="/img/icons/database.svg" width="25" />
                                    <span class="hide-on-minimize">Letter Campaigns</span>
                                </a>
                            </li>
                            <li>
                                <a href="/tenanted-properties" class="sidebar__nav-list-item pro-feature"
                                    :class="{ active: $route.path.includes('/tenanted-properties') }">
                                    <i class="ri-time-line" style="font-size: 25px; color: #fff;"></i>
                                    <span class="hide-on-minimize">Coming to market</span>
                                </a>
                            </li>
                            <li v-if="user.prospecting_tool_attributes && user.prospecting_tool_attributes.hasDatabase">
                                <a href="/prospecting-database" class="sidebar__nav-list-item pro-feature"
                                    :class="{ active: $route.path.includes('/prospecting-database') }">
                                    <img src="/img/icons/database.svg" width="25" />
                                    <span class="hide-on-minimize">My Database</span>
                                </a>
                            </li>
                            <li
                                :class="{ 'disabled': !(user.land_locator_attributes && user.land_locator_attributes.isActive) }">
                                <a href="/land-locator" class="sidebar__nav-list-item pro-feature"
                                    :class="{ active: $route.path.includes('/land-locator') }">
                                    <i class="ri-road-map-line" style="font-size: 25px; color: #fff;"></i>
                                    <span class="hide-on-minimize">Land Locator</span>
                                </a>
                            </li>
                        </ul>
                    </template>

                    <a class="d-flex justify-content-between mt-4 w-100 hide-on-minimize group-title" href="#"
                        @click.prevent="showAccountItems = !showAccountItems">
                        <span>Account</span>

                        <svg v-if="showAccountItems" class="flex-shrink-0" width="24" height="24"
                            xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                            <path
                                d="M11.9999 13.1714L16.9497 8.22168L18.3639 9.63589L11.9999 15.9999L5.63599 9.63589L7.0502 8.22168L11.9999 13.1714Z">
                            </path>
                        </svg>

                        <svg v-else class="flex-shrink-0" width="24" height="24" xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24" fill="currentColor">
                            <path
                                d="M13.1717 12.0007L8.22192 7.05093L9.63614 5.63672L16.0001 12.0007L9.63614 18.3646L8.22192 16.9504L13.1717 12.0007Z">
                            </path>
                        </svg>
                    </a>
                    <ul id="account-items" class="sidebar__nav-list ps-0 d-flex flex-column align-items-center mb-0">
                        <li v-if="user.is_company">
                            <a href="/agents/active" class="sidebar__nav-list-item"
                                :class="{ active: $route.name === 'agents' }">
                                <img src="/img/icons/agents.svg" width="25" />
                                <span class="hide-on-minimize">Negotiators</span>
                            </a>
                        </li>
                        <li>
                            <a href="/my-adverts" class="sidebar__nav-list-item"
                                :class="{ active: $route.path.includes('my-adverts') }">
                                <img src="/img/icons/posts.svg" width="25" />
                                <span class="hide-on-minimize">Adverts</span>
                            </a>
                        </li>
                        <li>
                            <a href="/profile" class="sidebar__nav-list-item"
                                :class="{ active: ['profile', 'public profile'].includes($route.name) }">
                                <img src="/img/icons/profile.svg" width="25" />
                                <span class="hide-on-minimize">Profile</span>
                            </a>
                        </li>
                    </ul>

                    <template v-if="!user.is_individual_tenant">
                        <a class="d-flex justify-content-between mt-4 w-100 hide-on-minimize group-title" href="#"
                            @click.prevent="showTenantAdminItems = !showTenantAdminItems">
                            <span>Tenant Admin</span>

                            <svg v-if="showTenantAdminItems" class="flex-shrink-0" width="24" height="24"
                                xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                                <path
                                    d="M11.9999 13.1714L16.9497 8.22168L18.3639 9.63589L11.9999 15.9999L5.63599 9.63589L7.0502 8.22168L11.9999 13.1714Z">
                                </path>
                            </svg>

                            <svg v-else class="flex-shrink-0" width="24" height="24" xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24" fill="currentColor">
                                <path
                                    d="M13.1717 12.0007L8.22192 7.05093L9.63614 5.63672L16.0001 12.0007L9.63614 18.3646L8.22192 16.9504L13.1717 12.0007Z">
                                </path>
                            </svg>

                        </a>
                        <ul id="tenant-admin-items"
                            class="sidebar__nav-list ps-0 d-flex flex-column align-items-center mb-0 pro-features">
                            <li v-if="user.is_company">
                                <a href="/lead-manager" class="sidebar__nav-list-item"
                                    :class="{ active: $route.path.includes('/lead-manager') }">
                                    <img src="/img/icons/prospecting-tool.svg" width="25" />
                                    <span class="hide-on-minimize">Lead Manager</span>
                                </a>
                            </li>
                            <li>
                                <a href="/tenant-referencing" class="sidebar__nav-list-item"
                                    :class="{ active: $route.path.includes('/tenant-referencing') }">
                                    <img src="/img/icons/database.svg" width="25" />
                                    <span class="hide-on-minimize">Tenant Referencing</span>
                                </a>
                            </li>
                        </ul>
                    </template>
                </div>
            </slot>

            <div class="app-links mt-auto hide-on-minimize mb-4">
                <a href="https://play.google.com/store/apps/details?id=com.krispyhouse.android"
                    class="app-links-google"></a>
                <a href="https://apps.apple.com/gb/app/krispyhouse/id1639538000" class="app-links-apple"></a>
            </div>
        </aside>
        <div class="sidebar-mask" />
    </div>
</template>

<script>
import Logo from '@components/partials/icons/Logo.vue';
import { matchRouteName } from '@helpers/page-state';
import resizeMixin from '@mixins/resizeMixin';
import { directive as onClickaway } from 'vue-clickaway2';
import { mapState } from 'vuex';
import { gsap } from 'gsap';
import { redirectTo } from '@helpers/url-handler';

export default {
    mixins: [resizeMixin],
    components: {
        Logo,
    },
    data() {
        return {
            isExpanded: false,
            shouldCloseSidebarByClick: true,
            showTenantAdminItems: true,
            showPremiumPlanItems: true,
            showAccountItems: false,
        };
    },
    mounted() {
        this.isExpanded = !this.isScreenLowerThanDesktop;

        this.$root.$on('toggleSidebar', (shouldCloseSidebarByClick, state) => {
            if (this.shouldCloseSidebarByClick && !shouldCloseSidebarByClick) return;
            this.shouldCloseSidebarByClick = shouldCloseSidebarByClick;
            this.isExpanded = state ?? !this.isExpanded;
            if (!this.isExpanded) this.shouldCloseSidebarByClick = false;
        });
    },
    watch: {
        user(user) {
            if (user) {
                this.$nextTick(() => {
                    gsap.to('#account-items', { opacity: 0, height: 0, duration: 0 });
                    gsap.to('#account-items .sidebar__nav-list-item', { height: 0, duration: 0 });
                    gsap.to('#account-items .sidebar__nav-list-item > *', { display: 'none', duration: 0 });
                });
            }
        },
        showAccountItems(show) {
            if (show) {
                gsap.to('#account-items .sidebar__nav-list-item', { height: 50, duration: 0 });
                gsap.to('#account-items .sidebar__nav-list-item > *', { display: 'initial', duration: 0 });
                if (this.user.is_individual_landlord) {
                    gsap.to('#account-items', { opacity: 1, height: 150, duration: 0.3 });
                } else gsap.to('#account-items', { opacity: 1, height: 150, duration: 0.3 });
            } else {
                gsap.to('#account-items', { opacity: 0, height: 0, duration: 0.3 });
                gsap.to('#account-items .sidebar__nav-list-item', { height: 0, duration: 0, delay: 0.3 });
                gsap.to('#account-items .sidebar__nav-list-item > *', { display: 'none', duration: 0, delay: 0.3 });
            }
        },
        showTenantAdminItems(show) {
            if (show) {
                gsap.to('#tenant-admin-items .sidebar__nav-list-item', { height: 50, duration: 0 });
                gsap.to('#tenant-admin-items .sidebar__nav-list-item > *', { display: 'initial', duration: 0 });
                gsap.to('#tenant-admin-items', { opacity: 1, height: 100, duration: 0.3 });
            } else {
                gsap.to('#tenant-admin-items', { opacity: 0, height: 0, duration: 0.3 });
                gsap.to('#tenant-admin-items .sidebar__nav-list-item', { height: 0, duration: 0, delay: 0.3 });
                gsap.to('#tenant-admin-items .sidebar__nav-list-item > *', { display: 'none', duration: 0, delay: 0.3 });
            }
        },
        showPremiumPlanItems(show) {
            if (show) {
                gsap.to('#premium-plan-items .sidebar__nav-list-item', { height: 50, duration: 0 });
                gsap.to('#premium-plan-items .sidebar__nav-list-item > *', { display: 'initial', duration: 0 });
                gsap.to('#premium-plan-items', { opacity: 1, height: 250, duration: 0.3 });
            } else {
                gsap.to('#premium-plan-items', { opacity: 0, height: 0, duration: 0.3 });
                gsap.to('#premium-plan-items .sidebar__nav-list-item', { height: 0, duration: 0, delay: 0.3 });
                gsap.to('#premium-plan-items .sidebar__nav-list-item > *', { display: 'none', duration: 0, delay: 0.3 });
            }
        },
    },
    computed: {
        ...mapState('user', ['user']),
        canUseCredits() {
            if (!(this.user && this.user.prospecting_tool_attributes && this.user.prospecting_tool_attributes.currentSubscriptionPlan && this.user.prospecting_tool_attributes.currentSubscriptionPlan.totalCredits)) return false;
            return true;
        },
        // A real Subscription, not a SubscriptionAttempt: currentSubscriptionPlan
        // falls back to the attempt, so it is true for people who never paid.
        hasSubscription() {
            return !!(this.user && this.user.prospecting_tool_attributes && this.user.prospecting_tool_attributes.hasSubscription);
        },
    },
    methods: {
        matchRouteName,
        redirectTo,
        openActivateProspectingIfDisabled(event) {
            if (!this.user.prospecting_tool_attributes) { event.preventDefault(); return; }
            if (this.user.prospecting_tool_attributes.isActive) return;
            event.preventDefault();
            this.redirectTo('/pricing');
        },
        onSidebarMouseEnter() {
            this.isExpanded = true;
        },
        onSidebarMouseLeave() {
            if (this.shouldCloseSidebarByClick) return;
            this.isExpanded = false;
        },
        onLetterCampaignsClick() {
            if (this.canUseCredits) { redirectTo('/letter-sending/overview'); return; }
            // Only Platinum ships with credits, so Premium subscribers land here with a
            // zero balance. They need to buy credits, not pick a different plan.
            if (this.hasSubscription) { redirectTo('/purchase-credits'); return; }
            redirectTo('/pricing', { showNotAvailableFeatureBanner: true });
        },
    },
    directives: {
        onClickaway,
    },
};
</script>
